import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { CheckCircle2, XCircle, RotateCcw, Eye, ClipboardCheck, Trophy } from 'lucide-react';
import { academyApi, STATUS_LABELS, STATUS_TONES, POSTE_LABELS } from './api';
import AcademyPracticalEvalDialog from './AcademyPracticalEvalDialog';

const AcademyValidationQueue = ({ onValidated }) => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, mode: null, enrollment: null });
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [practicalFor, setPracticalFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await academyApi.pendingValidations();
      setPending(data || []);
    } catch (e) {
      toast.error("Erreur lors du chargement.");
    } finally {
      setLoading(false);
    }
  };

  const isInternalPractical = (e) => {
    // Module SADA interne + quiz réussi + pas encore de certificat interne
    const org = (e.training?.organism || '').toLowerCase();
    const quizPassed = (e.history || []).some((h) => h.action === 'certificate_uploaded') === false
      && !!(e.certificate === null || e.certificate === undefined);
    // Fallback plus fiable via training.organism
    return org === 'sada academy';
  };

  useEffect(() => { load(); }, []);

  const openDialog = (mode, enrollment) => {
    setDialog({ open: true, mode, enrollment });
    setComment('');
  };

  const closeDialog = () => setDialog({ open: false, mode: null, enrollment: null });

  const submit = async () => {
    const { mode, enrollment } = dialog;
    if (!enrollment) return;
    if ((mode === 'reject' || mode === 'request') && comment.trim().length < 5) {
      toast.error("Merci d'écrire au moins 5 caractères.");
      return;
    }
    setSaving(true);
    try {
      if (mode === 'validate') {
        await academyApi.validateCertificate(enrollment.id, comment);
        toast.success("Certificat validé.");
      } else if (mode === 'reject') {
        await academyApi.rejectCertificate(enrollment.id, comment);
        toast.success("Certificat refusé.");
      } else if (mode === 'request') {
        await academyApi.requestNewCertificate(enrollment.id, comment);
        toast.success("Nouveau document demandé.");
      }
      closeDialog();
      load();
      if (onValidated) onValidated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    } finally {
      setSaving(false);
    }
  };

  const viewCert = async (enrollmentId) => {
    // Récupère blob avec header auth puis ouvre dans un nouvel onglet
    try {
      const t = localStorage.getItem('dermascan_token');
      const res = await axios.get(academyApi.downloadCertificateUrl(enrollmentId), {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${t}` },
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
    } catch (e) {
      toast.error("Impossible d'ouvrir le certificat.");
    }
  };

  return (
    <div className="space-y-4" data-testid="academy-validation-queue">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-amber-600" />
          Certificats en attente de validation
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 ml-2">{pending.length}</Badge>
        </h3>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>Rafraîchir</Button>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-6">Chargement…</div>
      ) : pending.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          Aucun certificat en attente. 👍
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {pending.map((e) => (
            <Card key={e.id} data-testid={`academy-pending-${e.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900">{e.user_name}</div>
                      {e.user_poste && <Badge variant="outline">{POSTE_LABELS[e.user_poste] || e.user_poste}</Badge>}
                      <Badge className={STATUS_TONES[e.status]}>{STATUS_LABELS[e.status]}</Badge>
                    </div>
                    <div className="text-sm text-slate-700 mt-1">
                      <strong>{e.training?.title}</strong> — {e.training?.organism || '—'}
                    </div>
                    {isInternalPractical(e) ? (
                      <div className="text-xs text-emerald-700 mt-0.5 font-medium">
                        Quiz réussi · en attente d&apos;évaluation pratique
                      </div>
                    ) : (
                      <>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Certificat déposé le {e.certificate?.uploaded_at?.slice(0, 10)}
                          {e.certificate?.number && <> · N° {e.certificate.number}</>}
                        </div>
                        <div className="text-xs text-slate-500">
                          Fichier : {e.certificate?.filename}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {isInternalPractical(e) ? (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPracticalFor(e)} data-testid={`academy-practical-eval-${e.id}`}>
                          <Trophy className="w-3.5 h-3.5 mr-1" /> Évaluation pratique
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openDialog('reject', e)} data-testid={`academy-reject-btn-${e.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => viewCert(e.id)} data-testid={`academy-view-cert-${e.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Voir
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openDialog('validate', e)} data-testid={`academy-validate-btn-${e.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider
                        </Button>
                        <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => openDialog('request', e)} data-testid={`academy-request-btn-${e.id}`}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Nouveau doc
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openDialog('reject', e)} data-testid={`academy-reject-btn-${e.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent data-testid="academy-validation-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog.mode === 'validate' && 'Valider le certificat'}
              {dialog.mode === 'reject' && 'Refuser le certificat'}
              {dialog.mode === 'request' && 'Demander un nouveau document'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              {dialog.enrollment?.user_name} — {dialog.enrollment?.training?.title}
            </p>
            <Textarea
              rows={4}
              placeholder={
                dialog.mode === 'validate'
                  ? 'Note (optionnelle)'
                  : dialog.mode === 'reject'
                  ? "Motif du refus (obligatoire)"
                  : "Précisez ce qui manque (obligatoire)"
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="academy-validation-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Annuler</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className={
                dialog.mode === 'validate'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : dialog.mode === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }
              data-testid="academy-validation-submit"
            >
              {saving ? 'Envoi…' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AcademyPracticalEvalDialog
        open={!!practicalFor}
        onClose={() => setPracticalFor(null)}
        enrollment={practicalFor}
        onValidated={() => { load(); if (onValidated) onValidated(); }}
      />
    </div>
  );
};

export default AcademyValidationQueue;
